import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useClients } from "@/hooks/useClients";
import { useFunds } from "@/hooks/useFunds";
import { useInvestments } from "@/hooks/useInvestments";
import { useAllInteractions } from "@/hooks/useInteractions";
import { useMe } from "@/hooks/useMe";
import { ClientsTable } from "@/components/ClientsTable";
import { ClientFormDialog } from "@/components/ClientFormDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { AlertCircle, Plus, Search, LogOut, TrendingUp, ArrowLeft } from "lucide-react";
import { CLIENT_ORIGINS, CLIENT_TYPES, type Client } from "@/types";
import { useAuth } from "@/auth/useAuth";

export default function Index() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const { data: me } = useMe();
  const isAdmin = me?.user.isAdmin ?? false;
  const canEditClients = isAdmin || (me?.permissions?.can_edit_clients ?? false);
  const canViewInvestments = isAdmin || (me?.permissions?.can_view_investments ?? false);
  const { data: clients, isLoading } = useClients();
  const { data: funds } = useFunds();
  const { data: investments } = useInvestments();
  const { data: interactions } = useAllInteractions();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [search, setSearch] = useState("");
  const [filterOrigin, setFilterOrigin] = useState<string>("all");
  const [filterType, setFilterType] = useState<string>("all");
  // Booleano, não string "all"/"pending": é um botão de alternância, e um estado
  // de dois valores não precisa de sentinela.
  const [soPendentes, setSoPendentes] = useState(false);

  // Clientes com ao menos uma interação com task preenchida e não concluída.
  //
  // ⚠️ Tem que ficar ANTES do `filtered`, e não depois como estava até 26/ago:
  // o callback do useMemo do `filtered` executa na hora e agora usa este Set,
  // então declarar depois daria erro de acesso antes da inicialização.
  const pendingTaskClientIds = useMemo(() => {
    const set = new Set<string>();
    (interactions ?? []).forEach((i) => {
      if (i.task && !i.task_completed) set.add(i.client_id);
    });
    return set;
  }, [interactions]);

  const filtered = useMemo(() => {
    if (!clients) return [];
    const s = search.toLowerCase();
    return clients.filter((c) => {
      const matchesSearch =
        !search ||
        c.name.toLowerCase().includes(s) ||
        c.person_of_contact.some((p) => p.toLowerCase().includes(s)) ||
        c.email.some((e) => e.toLowerCase().includes(s)) ||
        c.responsavel_jusc.toLowerCase().includes(s);
      const matchesOrigin = filterOrigin === "all" || c.origin === filterOrigin;
      const matchesType = filterType === "all" || c.type === filterType;
      // Mesma definição de "pendente" que o badge da lista usa — vem do mesmo
      // Set, então filtro e sinalização nunca podem discordar.
      const matchesTasks = !soPendentes || pendingTaskClientIds.has(c.id);
      return matchesSearch && matchesOrigin && matchesType && matchesTasks;
    });
  }, [clients, search, filterOrigin, filterType, soPendentes, pendingTaskClientIds]);

  const handleEdit = (client: Client) => {
    setEditingClient(client);
    setDialogOpen(true);
  };

  const handleNew = () => {
    setEditingClient(null);
    setDialogOpen(true);
  };

  const clearFilters = () => {
    setSearch("");
    setFilterOrigin("all");
    setFilterType("all");
    setSoPendentes(false);
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border" style={{ backgroundColor: "rgb(0, 56, 130)" }}>
        <div className="container mx-auto flex items-center justify-between py-5 px-4">
          <div
            className="flex items-center gap-3 cursor-pointer"
            onClick={() => navigate("/")}
          >
            <img src="/Logo JC Azul.png" alt="JusCapital" className="h-10 w-auto" />
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm hidden sm:inline" style={{ color: "rgba(255,255,255,0.7)" }}>
              {user?.email}
            </span>
            <Button
              variant="ghost"
              onClick={() => navigate(-1)}
              className="gap-2"
              style={{ color: "white" }}
            >
              <ArrowLeft className="h-4 w-4" /> Back
            </Button>
            {canViewInvestments && (
              <Button
                variant="ghost"
                onClick={() => navigate("/investments")}
                className="gap-2"
                style={{ color: "white" }}
              >
                <TrendingUp className="h-4 w-4" /> Investments
              </Button>
            )}
            {canEditClients && (
              <Button onClick={handleNew} className="gap-2">
                <Plus className="h-4 w-4" /> New Client
              </Button>
            )}
            <Button variant="ghost" size="icon" onClick={() => signOut()} title="Sign out" style={{ color: "white" }}>
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-6">
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by investor, POC, email or POC Jus..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <Select value={filterOrigin} onValueChange={setFilterOrigin}>
            <SelectTrigger className="w-full sm:w-44">
              <SelectValue placeholder="Group" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All groups</SelectItem>
              {CLIENT_ORIGINS.map((o) => (
                <SelectItem key={o} value={o}>{o}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-full sm:w-44">
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              {CLIENT_TYPES.map((t) => (
                <SelectItem key={t} value={t}>{t}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {/* Alternância, não Select: são dois estados, e o botão mostra o
              estado atual sem precisar abrir nada. `aria-pressed` é o que
              informa leitor de tela que isto liga e desliga em vez de navegar. */}
          <Button
            variant={soPendentes ? "default" : "outline"}
            aria-pressed={soPendentes}
            onClick={() => setSoPendentes((v) => !v)}
            className="w-full sm:w-auto gap-2"
          >
            <AlertCircle className="h-4 w-4" />
            Pending tasks
          </Button>
          {(search || filterOrigin !== "all" || filterType !== "all" || soPendentes) && (
            <Button variant="ghost" onClick={clearFilters}>Clear</Button>
          )}
        </div>

        <p className="text-sm text-muted-foreground mb-4">
          {isLoading ? "Loading..." : `${filtered.length} client(s)`}
        </p>

        {isLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        ) : (
          <ClientsTable
            clients={filtered}
            onEdit={handleEdit}
            funds={funds ?? []}
            investments={investments ?? []}
            pendingTaskClientIds={pendingTaskClientIds}
          />
        )}
      </div>

      <ClientFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        client={editingClient}
      />
    </div>
  );
}
